import rules from "@/utils/inputRules";
import { translation } from "@/utils/service/Translations/translations";
import { isFalse, isTrue } from "@/utils/service/DocumentGeneration/answers";
import {
    COOKIE_POLICY,
    TERMS_OF_SERVICE,
} from "@/utils/service/documents";
import {toUrl} from "@/utils/textUtilities";

export default [
    {
        name: "General Information",
        inputs: [
            {
                model: "company.type",
                type: "radio",
                default: "Service Provider",
                values: {
                    "Service Provider": "Individual",
                    Company: "Business"
                }
            },
            {
                model: "company.name",
                type: "businessName",
                classes: "col-md-6",
            },
            {
                model: "company.email",
                type: "email",
                classes: "col-md-6",
            },
            {
                model: "website_url",
                type: "websiteUrl",
                classes: "col-md-6",
            },
            {
                model: "company.country",
                type: "country",
                classes: "col-md-6",
            },
            {
                title:
                    "Please provide the email address for the questions related to Privacy Policy",
                tooltip:
                    "This email address will be used by your users to send you the requests or the questions related to your Privacy Policy",
                model: "company.policy_email",
                type: "email",
                input: {
                    global: false,
                },
            },
        ],
        actions: [
            {
                component: () => import("@/components/Projects/Project/ProjectGeneralInformationUpdateOnDocumentCreate.vue"),
                params: (self) => ({
                    color: "secondaryDark",
                    text: true,
                    class: "th-mr-12",
                    to: {
                        name: "project_index",
                        params: self?.$route.params,
                    },
                }),
                cta: "Edit project information",
            },
        ]
    },
    {
        name: "Terms of Services",
        inputs: [
            {
                title: "Do you have Terms of Services on your website?",
                model: "haveTOS",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => isTrue(model["haveTOS"]),
                title: "Please insert the link to your Terms of Service",
                model: "tos_link",
                type: "projectDocument",
                policyType: TERMS_OF_SERVICE,
            },
            {
                condition: (model) => isFalse(model["haveTOS"]),
                type: "custom",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                input: {
                    title: `We strongly recommend that you include a Terms of Service for your website`,
                },
            },
            {
                condition: (model) => isFalse(model["haveTOS"]),
                model: "tos_no_use_create",
                type: "projectDocumentCreateSuggest",
                policyType: TERMS_OF_SERVICE,
            },
        ],
    },
    {
        name: "Data Collection",
        title:
            "Please select what personal information your user provides you directly (e.g. when the user creates an account or signs up)",
        inputs: [
            {
                model: "collected_data.account_signup_info",
                type: "multiSelect",
                label: "Account sign up information",
                inline: true,
                default: [],
                values: {
                    email_address: "Email",
                    name: "Name",
                    surname: "Surname",
                    phone_number: "Phone",
                    user_name: "Username",
                    passwords: "Passwords",
                    personal_number: "Personal Number",
                    address: "Address",
                },
            },
            {
                model: "collected_data.payment_info",
                type: "multiSelect",
                label: "Payment information",
                inline: true,
                default: [],
                values: {
                    credit_card_numbers: "Credit or debit card number",
                    credit_or_debit_card_type: "Credit or debit card type",
                    credit_or_debit_card_expiration_date:
                        "Credit or debit card expiration date",
                    billing_address: "Billing address",
                    tax_number: "Tax number",
                    name_and_surname: "Name and surname",
                },
            },
            {
                model: "collected_data.login_info",
                type: "multiSelect",
                label: "Login information",
                inline: true,
                default: [],
                values: {
                    authentication: "Authentication Data",
                },
            },
            {
                model: "collected_data.communication_data",
                type: "multiSelect",
                label: "Communication",
                inline: true,
                default: [],
                values: {
                    communication_data: "Communication, Chats, Messaging",
                },
            },
            {
                model: "collected_data.other_info",
                type: "addOption",
                label: "Other",
                addLabel: "Enter information you collect",
                default: [],
                inline: true,
            },
        ],
    },
    {
        name: "Automatically Collected Data",
        title: "What information do you automatically collect?",
        tooltip:
            "In some instances, you and your service providers may use cookies, web beacons, and other technologies to automatically collect certain types of information when your user visits your website online.",
        inputs: [
            {
                model: "automatically_collected_data.tracking",
                type: "multiSelect",
                label: "Tracking technologies and Cookies",
                inline: true,
                default: [],
                values: {
                    cookies: "Cookies",
                    beacons: "Beacons",
                    tags: "Tags",
                    ci: "CI codes (click tracking)",
                    isc: "ISC (source tracking)",
                    itc: "ITC (item tracking codes)",
                    phone_model: "Phone model",
                    device_id: "Device ID",
                    customer_number: "Customer number",
                },
            },
            {
                model: "automatically_collected_data.device_log_data",
                type: "multiSelect",
                label: "Log data and device information",
                inline: true,
                default: [],
                values: {
                    ip: "Internet protocol (IP) addresses",
                    browser_type: "Browser type",
                    isp: "Internet service provider (ISP)",
                    exit_pages: "Referring/exit pages",
                    operating_system: "Operating system",
                    time_stamp: "Date/time stamp",
                    clickstream_data: "Clickstream data",
                },
            },
            {
                model: "automatically_collected_data.other",
                type: "multiSelect",
                inline: true,
                default: [],
                result: "object",
                values: {
                    geo_data: "Geo-location data",
                    usage_info: "Usage information with Google Analytics",
                    personal_info: "Publicly available personal information",
                },
            },
            {
                model: "automatically_collected_data.other_info",
                type: "addOption",
                label: "Other",
                addLabel: "Enter information automatically collected",
                inline: true,
                default: [],
                options: [],
            },
        ],
    },
    {
        name: "Data Usage",
        inputs: [
            {
                label: "How do you use information collected?",
                model: "info_usage",
                type: "multiSelect",
                default: [],
                values: [
                    "to identify user",
                    "to create account",
                    "to create trusted environment",
                    "to create statistics and analyze market",
                    "to stay connected",
                    "to customize marketing",
                    "to send billing information",
                    "to manage user orders",
                    "to contact user",
                    "to improve services",
                    "to ensure data security and prevent fraud",
                    "to comply with applicable laws",
                    "to request feedback",
                    "to post testimonials",
                    "to provide support",
                ],
            },
            {
                model: "info_usage_other",
                type: "addOption",
                label: "Other",
                addLabel: "Enter other ways you use information",
                default: [],
                inline: true,
            },
        ],
    },
    {
        name: "Direct Marketing",
        inputs: [
            {
                title:
                    "Do you use user's email to send marketing and promotional information?",
                model: "direct_marketing.use",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                title: "How your user can unsubscribe?",
                model: "direct_marketing.unsubscribe",
                condition: (model) => isTrue(model["direct_marketing.use"]),
                type: "multiSelect",
                result: "object",
                rules: [rules.atLeastOne],
                default: {
                    via_preferences: false,
                    via_instructions: false,
                    via_email: false,
                    via_link: false,
                },
                values: {
                    via_preferences:
                        "User can access his settings in the account and update his preferences any time.",
                    via_instructions:
                        "User can follow the instructions in the send email and unsubscribe any time.",
                    via_email: {
                        label:
                            "User can contact you by the email provided and ask to opt out.",
                        inputs: [
                            {
                                label: "Enter the unsubscribe email",
                                model: "unsubscribe_email",
                                type: "text",
                                class: "th-mt-12 th-mb-4 col-md-6",
                                condition: (model) =>
                                    model.find((k) => k === "via_email"),
                                rules: [rules.required, rules.email],
                            },
                        ],
                    },
                    via_link:
                        "User can click on the link in the received email to unsubscribe.",
                },
            },
        ],
        validate: (model) => {
            if (isTrue(model["direct_marketing.use"])) {
                const atLeastOneSelected =
                    Object.values(model["direct_marketing.unsubscribe"]).filter(
                        (v) => v
                    ).length <= 0
                        ? translation.t("At least one has to be selected")
                        : "";

                if (atLeastOneSelected) return atLeastOneSelected;

                if (
                    model["direct_marketing.unsubscribe"].via_email &&
                    rules.email(
                        model["direct_marketing.unsubscribe"][
                            "unsubscribe_email"
                        ]
                    ) !== true
                )
                    return translation.t("Please enter unsubscribe email");
            }

            return null;
        },
    },
    {
        name: "Third Party",
        inputs: [
            {
                title:
                    "Do you share information with third parties (i.e. vendors)?",
                model: "third_parties.use",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                title: "To whom you share information:",
                model: "third_parties",
                condition: (model) => isTrue(model["third_parties.use"]),
                type: "combobox",
                default: [],
                hint: "Simply search the list of companies or enter your own",
                values: [
                    { type: "Invoicing and Billing company", name: "PayPal" },
                    { type: "Invoicing and Billing company", name: "Stripe" },
                    { type: "Website hosting company", name: "HostGator" },
                    { type: "Website hosting company", name: "Bluehost" },
                    { type: "Website hosting company", name: "GoDaddy" },
                    {
                        type: "Social media and advertising",
                        name: "Google AdSense",
                    },
                    { type: "Social media and advertising", name: "Bing Ads" },
                    {
                        type: "Retargeting platforms",
                        name: "Facebook Custom Audience",
                    },
                    {
                        type: "Retargeting platforms",
                        name: "Google Ads Remarketing",
                    },
                    {
                        type: "Backup and security company",
                        name: "Dropbox Backup",
                    },
                    {
                        type: "Backup and security company",
                        name: "Google Drive Backup ",
                    },
                    { type: "Comments & Forums", name: "Facebook Comments" },
                    { type: "Comments & Forums", name: "Disqus" },
                    { type: "Business affiliates", name: "Amazon Affiliation" },
                    { type: "Business affiliates", name: "iTunes Affiliation" },
                    { type: "Shipping company", name: "HDL" },
                    { type: "Shipping company", name: " FedEx" },
                    { type: "Shipping company", name: " UPS" },
                    { type: "Analytics", name: "Google Analytics" },
                    {
                        type: "Communication platform provider",
                        name: "Facebook Customer Chat",
                    },
                    {
                        type: "Communication platform provider",
                        name: " LiveChat",
                    },
                ],
            },
        ],
    },
    {
        name: "Cookies",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title: "What is a cookie?",
                    subtitle:
                        "A cookie is a small text file placed onto your user's device that enables your website features and functionality. For example, cookies can enable you to identify your website's user's device and secure access to your website. Cookies also permit you to recognize users and avoid repetitive requests for the same information.",
                }),
            },
            {
                condition: (model) =>
                    isFalse(model["cookies.used"]) ||
                    model["cookies.policy_link"] === "dontuse",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title:
                        "We strongly recommend that you include a Cookie Policy for your website",
                }),
            },
        ],
        inputs: [
            {
                title: "Do you use cookies and other tracking technologies?",
                model: "cookies.used",
                type: "radio",
                default: "true",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => isTrue(model["cookies.used"]),
                title: "Please insert the link to your Cookie Policy",
                model: "cookies.policy_link",
                type: "projectDocument",
                values: {
                    dontuse: "I want to continue without one",
                },
                policyType: COOKIE_POLICY,
            },
            {
                condition: (model) =>
                    isTrue(model["cookies.used"]) &&
                    model["cookies.policy_link"] === "dontuse",
                model: "cookies.types",
                title: "What type of cookies do you use on your website?",
                type: "multiSelect",
                default: {
                    necessary: true,
                    functional: false,
                    performance: false,
                    targeted: false,
                },
                result: "object",
                values: {
                    necessary: {
                        label: "Necessary",
                        disabled: true,
                        tooltip:
                            "Strictly necessary cookies - usually these cookies are required for the operation of the website. They might help you to show the user the right information, customize his experience, and allow you to implement and maintain security features. Usually, without these cookies operation of the website is impossible or its functioning may be severely affected.",
                    },
                    functional: {
                        label: "Preferences",
                        tooltip:
                            "Functional cookies - usually these cookies are used to improve the functional performance of your website and make it easier for users to use. These cookies remember the settings selected by the users (for example, the settings of language and time zone). With the use of these cookies, the users may avoid changes of settings during each visit of the website.",
                    },
                    performance: {
                        label: "Statistics",
                        tooltip:
                            "Performance cookies - usually these cookies usually show if the user has visited your website before. The analytic cookies allow you to recognize and count the number of users of your website and see how such users navigate through your website.",
                    },
                    targeted: {
                        label: "Marketing",
                        tooltip:
                            "Marketing, targeting, and advertising cookies - usually these cookies record your visitor's visits to your website, the pages they have visited, and the links they have followed. This information is used to make the advertising displayed on it more relevant to your user's interests. The advertising cookies can let you know whether your user has already seen the specific advertisement or a certain type of advertisement.",
                    },
                },
            },
        ],
    },
    {
        name: "GDPR",
        inputs: [
            {
                title: "Do you monitor the behaviour of people who are in EU?",
                model: "gdpr.use1",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                title:
                    "Do you collect personal data of the users in EU, UK, Switzerland, Iceland, Liechtenstein, Norway, Singapore or Brazil?",
                model: "gdpr.use2",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) =>
                    isTrue(model["gdpr.use1"]) ||
                    isTrue(model["gdpr.use2"]),
                type: "custom",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                input: {
                    title:
                        "You must comply with GDPR and other privacy directives and may be required to appoint a local representative e.g. the European representative to act as your direct contact for data subjects and EU and EEA supervisory authorities. If you have already appointed a European representative please provide the details below",
                },
            },
            {
                condition: (model) =>
                    isTrue(model["gdpr.use1"]) ||
                    isTrue(model["gdpr.use2"]),
                title: "Full name of the representative",
                model: "gdpr_representative.name",
                type: "text",
                classes: "col-md-6",
                rules: [rules.required],
            },
            {
                condition: (model) =>
                    isTrue(model["gdpr.use1"]) ||
                    isTrue(model["gdpr.use2"]),
                title: "Contact details e.g. email, phone number",
                model: "gdpr_representative.contact",
                type: "text",
                classes: "col-md-6",
                rules: [rules.required],
            },
        ],
    },
    {
        name: "Minors Information",
        inputs: [
            {
                title:
                    "Do you collect information from minors (under 18 years)?",
                model: "minors_information",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => isTrue(model["minors_information"]),
                title:
                    "Is your website accessible to children under the age of 13?",
                model: "minors_information_under_13",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) =>
                    isTrue(model["minors_information_under_13"]),
                type: "custom",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                input: {
                    title:
                        "If your website is aimed at children under the age of 13, we strongly advise you to seek legal advice and do not use this service. The Children's Online Privacy Protection Act (COPPA) is difficult to comply with, and non-compliance will result in serious consequences, including civil penalties.",
                },
            },
        ],
    },
    {
        hide: (model) => isFalse(model["gdpr.use1"]) && isFalse(model["gdpr.use2"]),
        name: "Sensitive Information",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title:
                        "The following personal data is considered ‘sensitive’ and is subject to specific processing conditions under GDPR",
                    subtitle: [
                        "- personal data revealing racial or ethnic origin, political opinions, religious or philosophical beliefs;",
                        "- trade-union membership;",
                        "- genetic data, biometric data processed solely to identify a human being;",
                        "- health-related data;",
                        "- data concerning a person’s sex life or sexual orientation.",
                    ],
                }),
            },
        ],
        inputs: [
            {
                title:
                    "Do you collect sensitive information such as racial or ethnic origin, political opinions, religious or philosophical beliefs, genetic data, biometric data, health data or data related a sexual orientation?",
                model: "sensitive_information",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => isTrue(model["sensitive_information"]),
                type: "custom",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                input: {
                    title:
                        "If you collect sensitive information, we strongly advise you to seek legal advice and do not use this service. Sensitive data under GDPR is subject to specific processing conditions and non-compliance will result in serious consequences, including penalties",
                },
            },
        ],
    },
    {
        name: "Payments",
        inputs: [
            {
                title: "Do you accept payments in your website?",
                model: "payment_processor.use",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => isTrue(model["payment_processor.use"]),
                title:
                    "Do you use payment processor(s) (e.g. PayPal, Stripe, Braintree, Adyen) for payments processing?",
                model: "payment_processor.use_payment_processors",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) =>
                    isTrue(model["payment_processor.use"]) &&
                    isTrue(model["payment_processor.use_payment_processors"]),
                model: "payment_processor.payment_processors",
                type: "addToList",
                default: [],
                inputs: [
                    {
                        label: "Name",
                        model: "name",
                        type: "text",
                        rules: [rules.required],
                    },
                    {
                        label: "Privacy Policy URL",
                        model: "policy_link",
                        type: "text",
                        rules: [rules.url],
                        filters: [
                            (value) =>
                                toUrl(value)
                        ],
                    },
                ],
            },
        ],
    },
    {
        hide: (model) => isTrue(model["hide_third_party_links"]),
        name: "Advertisement",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title:
                        'Even if your website has only the link with your own social media account (e.g. Facebook, Twitter, Instagram), select "Yes"',
                }),
            },
        ],
        inputs: [
            {
                title:
                    "Do you have advertisements or links of any third party on your website?",
                model: "third_party_links",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
        ],
    },
    {
        name: "Retention",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title:
                        "What is considered personal information under California Consumer Privacy Act (CCPA)?",
                    subtitle: [
                        "Personal information under CCPA is information that identifies, relates to, describes, is reasonably capable of being associated with, or could reasonably be linked (directly or indirectly) with a particular consumer or household.",
                        "For example, it could include Your user's name, address, social security or passport number, email address, information about products purchased, browsing history, geolocation data, fingerprints, health information, and inferences from other personal information that could create a profile about Your user's preferences and characteristics.",
                    ],
                }),
            },
            {
                condition: model => isTrue(model["gdpr.use1"]) || isTrue(model["gdpr.use2"]),
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title:
                        "What is considered personal information under GDPR?",
                    subtitle: [
                        "Personal data means any information relating to an identified or identifiable natural person (‘data subject' - your user). An identifiable natural person is one who can be identified, directly or indirectly, in particular by reference to an identifier such as a name, an identification number, location data, an online identifier, or to one or more factors specific to the physical, physiological, genetic, mental, economic, cultural or social identity of that natural person.",
                        "Examples of personal data: names, addresses, emails, ID numbers, credit card or payment history information, photos or videos, fingerprint or other biometric information, IP address, geolocation information, data collected by cookies, browsing history, health information, or other information that links to a natural person.",
                    ],
                }),
            },
        ],
        inputs: [
            {
                title: "How long you keep user's personal information?",
                model: "retention.main",
                type: "radio",
                allowClear: true,
                default: "while_service_provided",
                values: {
                    while_account_exists: "As long as user has an account",
                    while_service_provided:
                        "As long as is necessary to provide services",
                    months_after_termination:
                            "Particular time after termination of the account",
                    months_after_abandonment: "Particular time after users account was abandoned",
                },
            },
            {
                label: "Enter the number of months",
                model: "retention.months_after_termination_period",
                type: "text",
                class: "th-mt-12 th-mb-4 col-md-6",
                condition: (model) =>
                    model["retention.main"] === "months_after_termination",
                rules: [rules.required],
            },
            {
                condition: (model) =>
                    model["retention.main"] === "months_after_abandonment",
                label: "Enter the number of months",
                model: "retention.months_after_abandonment_period",
                type: "text",
                class: "th-mt-12 th-mb-4 col-md-6",
                rules: [rules.required],
            },
            {
                model: "retention.other_cases",
                type: "addOption",
                addLabel: "Enter other options",
                default: [],
            },
        ],
        validate: (model) => {
            return model["retention.main"] || model["retention.other_cases"].length > 0
                ? ""
                : translation.t("At least one has to be selected or entered");
        },
    },
    {
        name: "Security",
        alerts: [
            {
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                params: () => ({
                    title: "What is considered appropriate security measures?",
                    subtitle:
                        "Appropriate security measures are intended to protect personal information against a breach of security leading to the accidental or unlawful destruction, loss, alteration, unauthorised disclosure of, or access to, personal data transmitted, stored or otherwise processed.",
                }),
            },
        ],
        inputs: [
            {
                title:
                    "Have you implemented appropriate security measures designed to protect the personal information you receive or collect?",
                model: "security",
                type: "radio",
                default: "false",
                values: {
                    true: "Yes",
                    false: "No",
                },
            },
            {
                condition: (model) => model["security"] !== "true",
                type: "custom",
                component: () =>
                    import("@/components/DocumentGeneration/Alert.vue"),
                input: {
                    title: "You are responsible for the security of the data received, please implement necessary security measures before you proceed further."
                }
            },
        ],
    },
];
